<script setup lang="ts">
import { useClientStore } from '../stores/clients'
import { useSaleStore } from '../stores/sales'
import { useInventoryStore } from '../stores/inventory'
import { computed, onMounted } from 'vue'
import { formatCurrency } from '../utils/format'
import { UiLoader, UiAlert } from '../components/ui'
import { useRouter } from 'vue-router'
import UiBarChart from '../components/charts/UiBarChart.vue'
import UiDoughnutChart from '../components/charts/UiDoughnutChart.vue'
import {
  format,
  subMonths,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'

const router = useRouter()

const clientStore = useClientStore()
const saleStore = useSaleStore()
const inventoryStore = useInventoryStore()

const isLoading = computed(() => clientStore.isLoading || saleStore.isLoading || inventoryStore.isLoading)
const error = computed(() => clientStore.error || saleStore.error || inventoryStore.error)

onMounted(() => {
  clientStore.fetchClients()
  saleStore.fetchSales()
  inventoryStore.fetchProducts()
})

const totalClients = computed(() => clientStore.clients.length)
const totalSales = computed(() => saleStore.sales.length)
const totalRevenue = computed(() =>
  saleStore.sales.reduce((sum, sale) => sum + sale.total, 0)
)
const totalStock = computed(() =>
  inventoryStore.products.reduce((sum, product) => sum + product.stock, 0)
)

const handleClick = (kpi: { label: string }) => {
  switch (kpi.label) {
    case 'Total de Clientes':
      router.push('/clients')
      break
    case 'Total de Vendas':
      router.push('/sales')
      break
    case 'Estoque Total':
      router.push('/inventory')
      break
  }
}

const kpis = computed(() => [
  {
    label: 'Total de Clientes',
    value: String(totalClients.value),
    icon: 'users',
    color: 'from-brand-500 to-brand-600',
    bg: 'bg-brand-100',
    textColor: 'text-brand-600',
  },
  {
    label: 'Total de Vendas',
    value: String(totalSales.value),
    icon: 'chart',
    color: 'from-emerald-500 to-emerald-600',
    bg: 'bg-emerald-100',
    textColor: 'text-emerald-600',
  },
  {
    label: 'Receita Total',
    value: formatCurrency(totalRevenue.value),
    icon: 'currency',
    color: 'from-violet-500 to-violet-600',
    bg: 'bg-violet-100',
    textColor: 'text-violet-600',
  },
  {
    label: 'Estoque Total',
    value: String(totalStock.value),
    icon: 'box',
    color: 'from-amber-500 to-amber-600',
    bg: 'bg-amber-100',
    textColor: 'text-amber-600',
  },
])

const CHART_PALETTE = [
  '#b45309', '#92400e', '#d97706', '#f59e0b', '#fbbf24',
  '#10b981', '#6366f1', '#ec4899', '#14b8a6', '#f97316',
]

// --- Top 5 produtos mais vendidos (por quantidade) ---
const top5Products = computed(() => {
  const totals = new Map<string, number>()
  for (const sale of saleStore.sales) {
    for (const item of sale.items) {
      totals.set(item.productId, (totals.get(item.productId) ?? 0) + item.quantity)
    }
  }
  return Array.from(totals.entries())
    .map(([productId, quantity]) => ({
      name: inventoryStore.getProduct(productId)?.name ?? 'Produto removido',
      quantity,
    }))
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 5)
})

const top5ProductsChart = computed(() => ({
  labels: top5Products.value.map(p => p.name),
  datasets: [
    {
      label: 'Quantidade vendida',
      data: top5Products.value.map(p => p.quantity),
      backgroundColor: CHART_PALETTE.slice(0, top5Products.value.length),
      borderRadius: 6,
      maxBarThickness: 40,
    },
  ],
}))

// --- Top 5 clientes que mais compraram (por receita total) ---
const top5Clients = computed(() => {
  const totals = new Map<string, { total: number; count: number }>()
  for (const sale of saleStore.sales) {
    const cur = totals.get(sale.clientId) ?? { total: 0, count: 0 }
    cur.total += sale.total
    cur.count += 1
    totals.set(sale.clientId, cur)
  }
  return Array.from(totals.entries())
    .map(([clientId, stat]) => ({
      name: clientStore.clients.find(c => c.id === clientId)?.name ?? 'Cliente removido',
      total: stat.total,
      count: stat.count,
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5)
})

const top5ClientsChart = computed(() => ({
  labels: top5Clients.value.map(c => c.name),
  datasets: [
    {
      data: top5Clients.value.map(c => c.total),
      backgroundColor: CHART_PALETTE.slice(0, top5Clients.value.length),
      borderWidth: 0,
    },
  ],
}))

// --- Comparativo de vendas dos últimos 3 meses ---
const last3Months = computed(() => {
  const now = new Date()
  const months = []
  for (let i = 2; i >= 0; i--) {
    const ref = subMonths(now, i)
    months.push({
      label: format(ref, 'MMM', { locale: ptBR }),
      start: startOfMonth(ref),
      end: endOfMonth(ref),
    })
  }
  return months
})

const salesByMonth = computed(() => {
  return last3Months.value.map(month => {
    const sales = saleStore.sales.filter(s =>
      isWithinInterval(s.date, { start: month.start, end: month.end })
    )
    return {
      count: sales.length,
      revenue: sales.reduce((sum, s) => sum + s.total, 0),
    }
  })
})

const salesComparisonChart = computed(() => {
  return {
    labels: last3Months.value.map(m => m.label),
    datasets: [
      {
        label: 'Receita (R$)',
        data: salesByMonth.value.map(s => s.revenue),
        backgroundColor: '#b45309',
        borderRadius: 6,
        maxBarThickness: 50,
        yAxisID: 'y',
      },
    ],
  }
})

const salesComparisonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom' as const,
      labels: { color: '#a8a29e', font: { size: 11 }, padding: 16, boxWidth: 12 },
    },
    tooltip: {
      backgroundColor: 'rgba(42, 32, 24, 0.95)',
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: (ctx: { dataIndex: number }) => {
          const item = salesByMonth.value[ctx.dataIndex]
          return [
            `Receita: ${formatCurrency(item.revenue)}`,
            `Nº de vendas: ${item.count}`,
          ]
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: '#a8a29e', font: { size: 12 } },
    },
    y: {
      beginAtZero: true,
      position: 'left' as const,
      grid: { color: 'rgba(168, 162, 158, 0.1)' },
      ticks: {
        color: '#a8a29e',
        font: { size: 11 },
        callback: (value: number | string) =>
          typeof value === 'number' ? `R$ ${Number(value).toFixed(0)}` : value,
      },
    },
  },
}

// --- Top 5 produtos próximos de estoque zerado ---
const lowStockProducts = computed(() => {
  return [...inventoryStore.products]
    .sort((a, b) => a.stock - b.stock)
    .slice(0, 5)
})

const lowStockChart = computed(() => ({
  labels: lowStockProducts.value.map(p => p.name),
  datasets: [
    {
      label: 'Estoque atual',
      data: lowStockProducts.value.map(p => p.stock),
      backgroundColor: lowStockProducts.value.map(p =>
        p.stock === 0 ? '#dc2626' : p.stock <= 5 ? '#f59e0b' : '#fbbf24'
      ),
      borderRadius: 6,
      maxBarThickness: 40,
    },
  ],
}))
</script>

<template>
  <div class="page-container">
    <div class="mb-6 sm:mb-8">
      <h1 class="text-xl font-bold text-surface-800 dark:text-surface-100 sm:text-3xl">Dashboard</h1>
      <p class="mt-1 text-xs text-surface-500 dark:text-surface-400 sm:text-sm">Visão geral do seu negócio</p>
    </div>

    <UiLoader v-if="isLoading && !error" text="Carregando dados..." />

    <UiAlert variant="error" :message="error || ''" />

    <div v-if="!isLoading && !error">
      <div
        class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
      >
        <div
          v-for="(kpi, index) in kpis"
          :key="index"
          class="kpi-card animate-slide-up cursor-pointer hover:scale-105 transition-transform duration-200"
          :style="{ animationDelay: `${index * 80}ms` }"
          @click="handleClick(kpi)"
        >
          <div class="flex items-start justify-between">
            <div>
              <p class="kpi-value" :class="kpi.textColor">{{ kpi.value }}</p>
              <p class="kpi-label">{{ kpi.label }}</p>
            </div>
            <div :class="['kpi-icon', kpi.bg]">
              <svg
                v-if="kpi.icon === 'users'"
                class="h-5 w-5"
                :class="kpi.textColor"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
              <svg
                v-else-if="kpi.icon === 'chart'"
                class="h-5 w-5"
                :class="kpi.textColor"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
              <svg
                v-else-if="kpi.icon === 'currency'"
                class="h-5 w-5"
                :class="kpi.textColor"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <svg
                v-else
                class="h-5 w-5"
                :class="kpi.textColor"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Comparativo de Vendas — últimos 3 meses</h2>
          </div>
          <div class="h-72">
            <UiBarChart :data="salesComparisonChart" :options="salesComparisonOptions" />
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Produtos próximos de zerar estoque</h2>
            <span class="badge badge-amber">Top 5 estoque baixo</span>
          </div>
          <div class="h-72">
            <UiBarChart :data="lowStockChart" />
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Top 5 Produtos mais vendidos</h2>
            <span class="badge badge-amber">por quantidade</span>
          </div>
          <div class="h-72">
            <UiBarChart :data="top5ProductsChart" />
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Top 5 Clientes que mais compraram</h2>
            <span class="badge badge-amber">por receita</span>
          </div>
          <div class="h-72">
            <UiDoughnutChart :data="top5ClientsChart" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>